import { useEffect, useRef, useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { mfaClient, recordMfaFactorReplacement, setMfaClientSession } from "../api/mfaAuthClient";
import { Button } from "../components/Button";
import { StatusBanner } from "../components/StatusBanner";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import { findCurrentFactor, replaceFactor, type NewFactor } from "./mfaReplacement";

type ViewState = "loading" | "ready" | "replacing" | "verifying" | "audit-error" | "success" | "error";
const CODE_LENGTH = 6;

function qrCodeSrc(qrCode: string): string {
  return qrCode.startsWith("data:image/") ? qrCode : `data:image/svg+xml;utf-8,${encodeURIComponent(qrCode)}`;
}

function errorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : "Não foi possível concluir a troca agora. Tente novamente.";
}

async function startFactorReplacement(options: {
  oldFactorId: string;
  online: boolean;
  currentAal: string;
  navigate: ReturnType<typeof useNavigate>;
  returnPath: string;
  confirm: () => boolean;
  setError: (message: string | null) => void;
  setView: (view: ViewState) => void;
  setNewFactor: (factor: NewFactor) => void;
}) {
  const { online, currentAal, navigate, returnPath, confirm, setError, setView, setNewFactor } = options;
  if (!online) return;
  if (currentAal !== "aal2") { navigate(`/mfa/desafio?retorno=${encodeURIComponent(returnPath)}`); return; }
  if (!confirm()) return;
  setError(null);
  setView("replacing");
  try {
    setNewFactor(await replaceFactor(mfaClient.auth.mfa));
    setView("verifying");
  } catch (cause) {
    setError(errorMessage(cause));
    setView("ready");
  }
}

async function submitFactorReplacement(options: {
  event: FormEvent<HTMLFormElement>;
  oldFactorId: string;
  newFactor: NewFactor;
  code: string;
  online: boolean;
  applyMfaVerifiedSession: (session: { accessToken: string; refreshToken: string; expiresIn: number }) => void;
  setCode: (code: string) => void;
  setAuditAccessToken: (token: string) => void;
  setError: (message: string | null) => void;
  setView: (view: ViewState) => void;
  expireSession: () => void;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const { event, oldFactorId, newFactor, code, online, applyMfaVerifiedSession, setCode, setAuditAccessToken, setError, setView, expireSession, navigate } = options;
  event.preventDefault();
  if (!online || code.length !== CODE_LENGTH) return;
  setError(null);
  setView("verifying");
  try {
    const verified = await verifyReplacementCode(newFactor.id, code);
    if (!verified) { setCode(""); setError("Código não confere. Confira o app do autenticador e tente novamente."); return; }
    applyMfaVerifiedSession({ accessToken: verified.access_token, refreshToken: verified.refresh_token, expiresIn: verified.expires_in });
    const { error: unenrollError } = await mfaClient.auth.mfa.unenroll({ factorId: oldFactorId });
    if (unenrollError) throw unenrollError;
    const audit = await recordMfaFactorReplacement(oldFactorId, newFactor.id, verified.access_token);
    if (audit.error) { setAuditAccessToken(verified.access_token); setError("O novo autenticador foi confirmado, mas o registro da troca não foi concluído. Tente registrar novamente."); setView("audit-error"); return; }
    setView("success");
    window.setTimeout(() => navigate("/dashboard", { replace: true }), 500);
  } catch (cause) {
    if ((cause as { status?: number }).status === 401) { expireSession(); return; }
    setError(errorMessage(cause));
  }
}

async function verifyReplacementCode(factorId: string, code: string) {
  const { data: challenge, error: challengeError } = await mfaClient.auth.mfa.challenge({ factorId });
  if (challengeError || !challenge) throw challengeError ?? new Error("Não foi possível iniciar a confirmação.");
  const { data, error } = await mfaClient.auth.mfa.verify({ factorId, challengeId: challenge.id, code });
  return error || !data ? null : data;
}

function MfaReplacementView(props: {
  view: ViewState;
  error: string | null;
  online: boolean;
  newFactor: NewFactor | null;
  code: string;
  codeRef: React.RefObject<HTMLInputElement>;
  setCode: (code: string) => void;
  onStart: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onRetryAudit: () => void;
}) {
  const { view, error, online, newFactor, code, codeRef, setCode, onStart, onSubmit, onRetryAudit } = props;
  return (
    <main className="centered-page mfa-challenge-page">
      <section className="card mfa-challenge-card" aria-labelledby="mfa-replacement-title">
        <p className="mfa-challenge__eyebrow">Ciclo de vida do autenticador</p>
        <h1 id="mfa-replacement-title">Trocar autenticador</h1>
        <p className="card__subtitle">Confirme o autenticador atual para cadastrar e confirmar um novo. O fator antigo só será invalidado depois da confirmação.</p>
        <MfaReplacementStatus view={view} error={error} online={online} onStart={onStart} onRetryAudit={onRetryAudit} />
        {newFactor && (view === "verifying" || view === "audit-error") && <MfaReplacementForm factor={newFactor} code={code} codeRef={codeRef} online={online} verifying={view === "verifying"} setCode={setCode} onSubmit={onSubmit} />}
      </section>
    </main>
  );
}

function MfaReplacementStatus(props: { view: ViewState; error: string | null; online: boolean; onStart: () => void; onRetryAudit: () => void }) {
  const { view, error, online, onStart, onRetryAudit } = props;
  return (
    <>
      {error && <StatusBanner tone="error">{error}</StatusBanner>}
      {view === "loading" && <p role="status">Preparando a troca…</p>}
      {view === "error" && <Button type="button" onClick={() => window.location.reload()} disabled={!online}>Tentar novamente</Button>}
      {view === "ready" && <Button type="button" onClick={onStart} disabled={!online}>Confirmar e trocar</Button>}
      {view === "audit-error" && <Button type="button" onClick={onRetryAudit} disabled={!online}>Tentar registrar a troca novamente</Button>}
      {view === "replacing" && <p role="status">Preparando o novo autenticador…</p>}
      {view === "success" && <p role="status">Autenticador trocado. Redirecionando…</p>}
    </>
  );
}

function MfaReplacementForm(props: { factor: NewFactor; code: string; codeRef: React.RefObject<HTMLInputElement>; online: boolean; verifying: boolean; setCode: (code: string) => void; onSubmit: (event: FormEvent<HTMLFormElement>) => void }) {
  const { factor, code, codeRef, online, verifying, setCode, onSubmit } = props;
  return (
    <>
      <img className="mfa-replacement__qr" src={qrCodeSrc(factor.qrCode)} alt="QR code para configurar o novo aplicativo autenticador" width={200} height={200} />
      <p>Escaneie o QR code ou informe esta chave manualmente: <strong className="mfa-enroll__secret">{factor.secret}</strong></p>
      <form onSubmit={onSubmit} noValidate>
        <label className="field__label" htmlFor="mfa-replacement-code">Código do aplicativo autenticador</label>
        <input ref={codeRef} id="mfa-replacement-code" className="field__input" inputMode="numeric" autoComplete="one-time-code" maxLength={CODE_LENGTH} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH))} />
        <Button type="submit" loading={verifying} disabled={!online || code.length !== CODE_LENGTH}>Confirmar novo autenticador</Button>
      </form>
    </>
  );
}

export function MfaReplacementPage() {
  const { state, expireSession, applyMfaVerifiedSession } = useAuth();
  const online = useOnlineStatus();
  const navigate = useNavigate();
  const location = useLocation();
  const codeRef = useRef<HTMLInputElement>(null);
  const [view, setView] = useState<ViewState>("loading");
  const [oldFactorId, setOldFactorId] = useState<string | null>(null);
  const [newFactor, setNewFactor] = useState<NewFactor | null>(null);
  const [code, setCode] = useState("");
  const [auditAccessToken, setAuditAccessToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (state.status !== "authenticated") return;
    let cancelled = false;
    void setMfaClientSession(state.accessToken, state.refreshToken)
      .then(() => findCurrentFactor(mfaClient.auth.mfa))
      .then((result) => {
        if (cancelled) return;
        if (result.kind === "error") { setError(result.message); setView("error"); return; }
        setOldFactorId(result.factorId);
        setView("ready");
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        if ((cause as { status?: number }).status === 401) { expireSession(); return; }
        setError("Não foi possível preparar a troca de autenticador agora. Tente novamente.");
        setView("error");
      });
    return () => { cancelled = true; };
  }, [expireSession, state.status === "authenticated" ? state.accessToken : null]);

  useEffect(() => {
    if (view === "verifying") codeRef.current?.focus();
  }, [view]);

  if (state.status !== "authenticated") return null;
  const currentAal = state.aal;

  async function startReplacement() {
    if (!oldFactorId) return;
    await startFactorReplacement({ oldFactorId, online, currentAal: currentAal.currentLevel, navigate, returnPath: location.pathname, confirm: () => window.confirm("Trocar o autenticador atual? O fator antigo será invalidado após a confirmação do novo."), setError, setView, setNewFactor });
  }

  async function submitCode(event: FormEvent<HTMLFormElement>) {
    if (!oldFactorId || !newFactor) return;
    await submitFactorReplacement({ event, oldFactorId, newFactor, code, online, applyMfaVerifiedSession, setCode, setAuditAccessToken, setError, setView, expireSession, navigate });
  }

  async function retryAudit() {
    if (!oldFactorId || !newFactor || !auditAccessToken || !online) return;
    setError(null);
    const audit = await recordMfaFactorReplacement(oldFactorId, newFactor.id, auditAccessToken);
    if (audit.error) {
      setError("O novo autenticador foi confirmado, mas o registro da troca não foi concluído. Tente novamente.");
      return;
    }
    setView("success");
    window.setTimeout(() => navigate("/dashboard", { replace: true }), 500);
  }

  return <MfaReplacementView view={view} error={error} online={online} newFactor={newFactor} code={code} codeRef={codeRef} setCode={setCode} onStart={() => void startReplacement()} onSubmit={(event) => void submitCode(event)} onRetryAudit={() => void retryAudit()} />;
}
